import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from './Header';
import SideNav from './SideNav';
import Footer from './Footer';
import PageHeader from './PageHeader';
import SpeakingSkill from './SpeakingSkill';
import { toggleMute, speakWithAvatar, stopAvatarSpeech, isAvatarSpeaking } from '../ttsPlayer';
import { mapLanguage, mapLevel } from '../utils/languageUtils';
import usePageTimeTracker from '../hooks/usePageTimeTracker';
import './Common.css';
import './Practice.css';

const SpeakingPage = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { 
    language: initialLanguage = 'german', 
    proficiencyLevel: initialLevel = 'beginner', 
    scenario = 'rwsl',
    dialect = '',
    avatar: initialAvatar = 'soren'
  } = location.state || {};
  
  // Normalize language to 2-letter code for PageHeader
  const normalizeLanguageCode = (lang) => {
    const languageMap = {
      'german': 'de',
      'english': 'en',
      'french': 'fr',
      'spanish': 'es',
      'italian': 'it',
      'japanese': 'ja',
      'de': 'de',
      'en': 'en',
      'fr': 'fr',
      'es': 'es',
      'it': 'it',
      'ja': 'ja'
    };
    return languageMap[lang.toLowerCase()] || 'de';
  };

  const [language, setLanguage] = useState(normalizeLanguageCode(initialLanguage));
  const [proficiencyLevel, setProficiencyLevel] = useState(initialLevel);
  const [isMuted, setIsMuted] = useState(false);
  const [currentAvatar, setCurrentAvatar] = useState(initialAvatar || 'Max-business.mp4'); // Default to Soren
  const [isReadingWelcome, setIsReadingWelcome] = useState(false);

  // Convert language to ISO code format for PageHeader
  const languageToISOCode = (lang) => {
    const mapping = {
      'german': 'de',
      'english': 'en',
      'french': 'fr',
      'spanish': 'es',
      'italian': 'it',
      'japanese': 'ja',
      'de': 'de',
      'en': 'en',
      'fr': 'fr',
      'es': 'es',
      'it': 'it',
      'ja': 'ja'
    };
    return mapping[lang.toLowerCase()] || 'de';
  };

  // Convert proficiency level to kebab-case format for PageHeader
  const levelToKebabCase = (level) => {
    const mapping = {
      'beginner': 'beginner-a1',
      'elementary': 'elementary-a2',
      'pre-intermediate': 'pre-intermediate-b1',
      'intermediate': 'intermediate-b2',
      'advanced': 'advanced-c1',
      'proficient': 'proficient-c2',
      'beginner-a1': 'beginner-a1',
      'elementary-a2': 'elementary-a2',
      'pre-intermediate-b1': 'pre-intermediate-b1',
      'intermediate-b2': 'intermediate-b2',
      'advanced-c1': 'advanced-c1',
      'proficient-c2': 'proficient-c2',
      // Japanese JLPT levels
      'beginner-n5': 'beginner-n5',
      'elementary-n4': 'elementary-n4',
      'pre-intermediate-n3': 'pre-intermediate-n3',
      'intermediate-n2': 'intermediate-n2',
      'advanced-n1': 'advanced-n1'
    };
    return mapping[level.toLowerCase()] || level;
  };
  usePageTimeTracker('practice-speaking', user);
  const [promptTextForSpeaking, setPromptTextForSpeaking] = useState('');
  const [showSkillDropdown, setShowSkillDropdown] = useState(false);
  const skillDropdownRef = useRef(null);
  const [prompts, setPrompts] = useState([]);
  const [isLoadingPrompts, setIsLoadingPrompts] = useState(true);
  const [hasStartedConversation, setHasStartedConversation] = useState(false);
  const [showPromptsOverlay, setShowPromptsOverlay] = useState(false);
  const [showAllPrompts, setShowAllPrompts] = useState(false);
  const [showTranslation, setShowTranslation] = useState(false);
  const [translatingText, setTranslatingText] = useState('');
  const [translatedText, setTranslatedText] = useState('');
  const avatarVideoRef = useRef(null);
  const translationPanelRef = useRef(null);

  // Skill options for dropdown
  const skills = [
    { value: 'reading', label: 'Reading', route: '/practice-reading' },
    { value: 'writing', label: 'Writing', route: '/practice-writing' },
    { value: 'listening', label: 'Listening', route: '/practice-listening' },
    { value: 'speaking', label: 'Speaking', route: '/practice-speaking' }
  ];

  const currentSkill = 'speaking';

  // Helper function to get full language name
  const getLanguageName = () => {
    const languageMap = {
      'de': 'German', 'german': 'German',
      'en': 'English', 'english': 'English',
      'fr': 'French', 'french': 'French',
      'es': 'Spanish', 'spanish': 'Spanish',
      'it': 'Italian', 'italian': 'Italian',
      'ja': 'Japanese', 'japanese': 'Japanese'
    };
    return languageMap[(language || '').toLowerCase()] || 'German';
  };

  // Fetch prompts from database
  useEffect(() => {
    let cancelled = false;
    const fetchPrompts = async () => {
      const mappedLanguage = mapLanguage(language);
      let mappedLevel = mapLevel(proficiencyLevel);

      // Fix level-language mismatch: use default level if JLPT with non-Japanese or vice versa
      const isJLPT = /^N[1-5]$/i.test(mappedLevel);
      const isJapanese = language === 'ja' || language === 'japanese';
      if (isJLPT && !isJapanese) mappedLevel = 'A1';
      if (!isJLPT && isJapanese && mappedLevel) mappedLevel = 'N5';
      
      // Fetch from database (works in both local and production)
      try {
        setIsLoadingPrompts(true);
        console.log('Fetching prompts for:', { skill: 'Speaking', language: mappedLanguage, level: mappedLevel });
        
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001/lb-api';
        const response = await axios.post(
          `${apiUrl}/practice/prompts`,
          {
            skill: 'Speaking',
            language: mappedLanguage,
            proficiencyLevel: mappedLevel
          },
          { withCredentials: true }
        );
        
        if (cancelled) return;
        console.log('Prompts API response:', response.data);
        
        if (response.data.success && response.data.prompts) {
          // Extract text from prompt objects
          const promptTexts = response.data.prompts.map(p => p.text);
          setPrompts(promptTexts);
        } else {
          console.error('No prompts in response:', response.data);
          // Fallback to default prompts
          setPrompts([
            `Tell me about yourself (self-introduction) in ${mappedLanguage}`,
            `What are your favorite foods? (food preferences) in ${mappedLanguage}`,
            `Tell me about your family and friends in ${mappedLanguage}`
          ]);
        }
      } catch (error) {
        if (cancelled) return;
        console.error('Error fetching prompts:', error);
        console.error('Error response:', error.response?.data);
        // Fallback to default prompts
        setPrompts([
          `Tell me about yourself (self-introduction) in ${mappedLanguage}`,
          `What are your favorite foods? (food preferences) in ${mappedLanguage}`,
          `Tell me about your family and friends in ${mappedLanguage}`
        ]);
      } finally {
        if (!cancelled) setIsLoadingPrompts(false);
      }
    };

    fetchPrompts();
    return () => { cancelled = true; };
  }, [language, proficiencyLevel]);

  // Ensure video is paused on mount
  useEffect(() => {
    if (avatarVideoRef.current) {
      avatarVideoRef.current.pause();
      avatarVideoRef.current.currentTime = 0;
    }
  }, []);

  const handleSkillChange = (skill) => {
    setShowSkillDropdown(false);
    if (skill.value !== currentSkill) {
      // Navigate with state instead of URL parameters
      navigate(skill.route, {
        state: {
          language,
          proficiencyLevel,
          scenario,
          dialect,
          avatar: currentAvatar
        }
      });
    }
  };

  const handlePromptClick = (prompt) => {
    setPromptTextForSpeaking(prompt);
    setShowPromptsOverlay(false);
    // Don't hide welcome section yet - only hide after user actually submits
    // Reset after a short delay to allow SpeakingSkill to process
    setTimeout(() => setPromptTextForSpeaking(''), 100);
  };

  const handleNewChat = () => {
    // Reload the page to start fresh
    window.location.reload();
  };

  const breadcrumbs = [
    { label: 'Home', path: '/', current: false },
    { label: 'Explore Learning', path: '/explore-learning', current: false },
    { label: 'Practice', path: '/explore-learning', current: false }, // Goes to explore-learning
    { label: 'Speaking', current: true }
  ];

  const handleLanguageChange = (newLanguage) => {
    // Convert ISO code back to full language name
    const isoToLanguage = {
      'de': 'german',
      'en': 'english',
      'fr': 'french',
      'es': 'spanish',
      'it': 'italian',
      'ja': 'japanese'
    };
    setLanguage(isoToLanguage[newLanguage] || newLanguage);
  };

  const handleLevelChange = (newLevel) => {
    // Just pass through the kebab-case level directly — mapLevel handles all formats
    setProficiencyLevel(newLevel);
  };

  const handleAvatarSoundToggle = () => {
    toggleMute();
    setIsMuted(!isMuted);
  };

  // Translation function for welcome message (English → selected language)
  const handleTranslateWelcome = async () => {
    const welcomeText = `Hi, I am ${currentAvatar && (currentAvatar.includes('Max') || currentAvatar.toLowerCase().includes('soren')) ? 'Soren' : 'Nebula'}, let's practice ${getLanguageName()}.`;
    setTranslatingText(welcomeText);
    setShowTranslation(true);
    setTranslatedText('Translating...');
    
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001/lb-api';
    try {
      const response = await axios.post(`${apiUrl}/translate`, {
        text: welcomeText,
        source_language: 'English',
        target_language: getLanguageName(),
        level: proficiencyLevel || 'A1',
        context: 'speaking practice'
      }, { withCredentials: true });
      
      setTranslatedText(response.data?.translation || response.data?.translatedText || 'Translation not available');
    } catch (error) {
      console.error('Translation error:', error);
      setTranslatedText('Translation service unavailable. Please try again later.');
    }
  };

  const handleCloseTranslation = () => {
    setShowTranslation(false);
    setTranslatingText('');
    setTranslatedText('');
  };

  // Close translation panel when clicking outside
  useEffect(() => {
    if (!showTranslation) return;
    const handleClickOutside = (e) => {
      if (translationPanelRef.current && !translationPanelRef.current.contains(e.target)) {
        handleCloseTranslation();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showTranslation]);

  // Close skill dropdown when clicking outside
  useEffect(() => {
    if (!showSkillDropdown) return;
    const handleClickOutside = (e) => {
      if (skillDropdownRef.current && !skillDropdownRef.current.contains(e.target)) {
        setShowSkillDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSkillDropdown]);

  // Read welcome message aloud with English voice (since it's English text)
  const handleReadWelcome = async () => {
    if (isReadingWelcome) {
      stopAvatarSpeech();
      setIsReadingWelcome(false);
      return;
    }
    stopAvatarSpeech();
    setIsReadingWelcome(true);
    const avatarLower = String(currentAvatar).toLowerCase();
    const isMale = avatarLower.includes('max') || avatarLower.includes('soren');
    const englishVoice = isMale ? 'Matthew' : 'Joanna';
    const welcomeText = `Hi, I am ${isMale ? 'Soren' : 'Nebula'}, let's practice ${getLanguageName()}.`;
    try {
      await speakWithAvatar(welcomeText, null, englishVoice);
    } catch (error) {
      console.error('TTS error:', error);
      setIsReadingWelcome(false);
    }
  };

  // Check TTS completion for welcome message
  useEffect(() => {
    if (!isReadingWelcome) return;
    const check = setInterval(() => {
      if (!isAvatarSpeaking()) {
        setIsReadingWelcome(false);
        clearInterval(check);
      }
    }, 500);
    return () => clearInterval(check);
  }, [isReadingWelcome]);

  const handleChangeAvatar = () => {
    // Correct avatar mapping:
    // Soren (male) = 'Max-business.mp4'
    // Nebula (female) = 'talkingavater.mp4'
    const isSoren = currentAvatar.includes('Max') || currentAvatar.toLowerCase().includes('soren');
    const newAvatar = isSoren ? 'talkingavater.mp4' : 'Max-business.mp4';
    setCurrentAvatar(newAvatar);
    
    if (avatarVideoRef.current) {
      avatarVideoRef.current.src = `${process.env.PUBLIC_URL}/${newAvatar}`;
      avatarVideoRef.current.load();
    }
  };

  return (
    <div className="practice-page speaking-page">
      <Header user={user} />
      
      <div className="nav-header-container">
        <SideNav />
        <PageHeader 
          breadcrumbs={breadcrumbs}
          language={languageToISOCode(language)}
          proficiencyLevel={levelToKebabCase(proficiencyLevel)}
          onLanguageChange={handleLanguageChange}
          onLevelChange={handleLevelChange}
          showProgress={false}
        />
      </div>

      <main className="practice-content">
        {/* Practice Title */}
        <div className="practice-title-section">
          <div className="practice-title-row">
            <h1 className="practice-title">
              Practice:
              <span className="practice-skill-dropdown" ref={skillDropdownRef}>
                <span 
                  className="practice-skill-selector"
                  onClick={() => setShowSkillDropdown(!showSkillDropdown)}
                >
                  <span className="practice-skill-name">Speaking</span>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M6 9L12 15L18 9" stroke="#A100FF" strokeWidth="2"/>
                  </svg>
                </span>
                {showSkillDropdown && (
                  <div className="practice-skill-dropdown-menu">
                    {skills.map(skill => (
                      <div
                        key={skill.value}
                        className={`practice-skill-dropdown-item ${currentSkill === skill.value ? 'active' : ''}`}
                        onClick={() => handleSkillChange(skill)}
                      >
                        {skill.label}
                      </div>
                    ))}
                  </div>
                )}
              </span>
            </h1>
            <button 
              className={`practice-new-chat-btn ${hasStartedConversation ? 'active' : ''}`}
              onClick={handleNewChat}
              disabled={!hasStartedConversation}
            >
              New Chat
            </button>
          </div>
          <div className="practice-title-line"></div>
        </div>

        {/* Main Content Area */}
        <div className="practice-main-area">
          {/* Left Sidebar - Avatar Card */}
          <div className="practice-left-sidebar">
            <div className="practice-avatar-card">
              <img 
                src={currentAvatar && (currentAvatar.includes('Max') || currentAvatar.toLowerCase().includes('soren')) 
                  ? '/images/maleavatarinskillpage.png' 
                  : '/images/femaleavatrinskillpage.png'}
                alt={`${currentAvatar && (currentAvatar.includes('Max') || currentAvatar.toLowerCase().includes('soren')) ? 'Soren' : 'Nebula'} Avatar`}
                className="practice-avatar-image"
              />
              <div className="practice-avatar-info">
                <h2 className="practice-avatar-name">
                  {currentAvatar && (currentAvatar.includes('Max') || currentAvatar.toLowerCase().includes('soren')) ? 'Soren' : 'Nebula'}
                </h2>
                <button className="practice-change-avatar" onClick={handleChangeAvatar}>
                  Change Avatar
                </button>
              </div>
            </div>
          </div>

          {/* Right Content - Messages */}
          <div className="practice-right-content">
            {/* Welcome Section - Only shown before conversation starts */}
            {!hasStartedConversation && (
              <div className="practice-agent-section">
                <div className="practice-agent-avatar">
                  <span>{currentAvatar && (currentAvatar.includes('Max') || currentAvatar.toLowerCase().includes('soren')) ? 'S' : 'N'}</span>
                </div>
                <div className="practice-agent-info">
                  <p className="practice-welcome-message">
                    Hi, I am {currentAvatar && (currentAvatar.includes('Max') || currentAvatar.toLowerCase().includes('soren')) ? 'Soren' : 'Nebula'}, let's practice {getLanguageName()}.
                  </p>
                  <div className="practice-actions">
                    <button className="practice-translate-chip" onClick={handleTranslateWelcome}>
                      <img src="/images/translateiconactive.svg" alt="Translate" width="18" height="18" />
                      Translate in {getLanguageName()}
                    </button>
                    <button 
                      className="practice-translate-chip" 
                      onClick={handleReadWelcome}
                      style={{ marginLeft: '8px' }}
                    >
                      <img src={isReadingWelcome ? "/images/muteicon.svg" : "/images/readicon.svg"} alt="Say It Aloud" width="18" height="15" />
                      {isReadingWelcome ? 'Stop' : 'Say It Aloud'}
                    </button>
                  </div>

                  {/* Prompts Section */}
                  <div className="practice-prompts-section">
                    <div className="practice-prompts-header">
                      <img src="/images/Group.svg" alt="Prompt icon" width="12" height="15" />
                      <span>Explore Prompts</span>
                    </div>
                    <div className="practice-prompt-chips">
                      {isLoadingPrompts ? (
                        <div style={{ padding: '8px', color: '#707070' }}>Loading prompts...</div>
                      ) : prompts.length > 0 ? (
                        prompts.slice(0, showAllPrompts ? prompts.length : 3).map((prompt, index) => (
                          <button 
                            key={index} 
                            className="practice-prompt-chip"
                            onClick={() => handlePromptClick(prompt)}
                          >
                            {prompt}
                          </button>
                        ))
                      ) : (
                        <div style={{ padding: '8px', color: '#707070' }}>No prompts available</div>
                      )}
                    </div>
                    {prompts.length > 3 && (
                      <button 
                        className="practice-view-more-link"
                        onClick={() => setShowAllPrompts(!showAllPrompts)}
                      >
                        <img 
                          src="/images/downarrowicon.png" 
                          alt="" 
                          width="16" 
                          height="16" 
                          style={{transform: showAllPrompts ? 'rotate(180deg)' : 'none'}} 
                        />
                        {showAllPrompts ? 'View less' : 'View more'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Messages Area */}
            <div className="practice-messages">
              <SpeakingSkill 
                user={user}
                language={language}
                avatar={currentAvatar}
                proficiencyLevel={proficiencyLevel}
                avatarVideoRef={avatarVideoRef}
                promptText={promptTextForSpeaking}
                onConversationStart={() => setHasStartedConversation(true)}
                showPromptsOverlay={showPromptsOverlay}
                setShowPromptsOverlay={setShowPromptsOverlay}
                prompts={prompts}
                hasStartedConversation={hasStartedConversation}
                isLoadingPrompts={isLoadingPrompts}
              />
            </div>
          </div>

          {/* Translation Panel */}
          {showTranslation && (
            <div className="translation-panel" ref={translationPanelRef}>
              <div className="translation-header">
                <h3>Translation</h3>
                <button className="close-button" onClick={handleCloseTranslation}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" fill="#000000"/>
                  </svg>
                </button>
              </div>
              <div className="translation-content">
                <div className="translation-section">
                  <h4>{getLanguageName()} Translation</h4>
                  <div style={{whiteSpace: 'pre-wrap', lineHeight: '1.6'}}>
                    {translatedText}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default SpeakingPage;
